"use client";
import { useContext, useEffect, useRef, useState } from "react";
import Button from "./Button";
import { AppContext } from "./AppContext";
import { icons } from "./icons";
import {
	deepCopy,
	getColorName,
	saveMesh,
	validateString,
} from "@/functions/fns";
import ReactDOM from "react-dom";
import { names } from "./defaults";
import useUser from "@/hooks/useUser";
import Login from "@/components/Login";
import editData from "@/firebase/firestore/editData";
import deleteData from "@/firebase/firestore/deleteData";

export default function SaveDB() {
	const {
		config,
		bg,
		duration,
		animationType,
		receivedData,
		meshName,
		setIsSaving,
	} = useContext(AppContext);

	useEffect(() => {
		setName(names[Math.floor(Math.random() * names.length)]);
	}, [config, bg]);

	const [open, setOpen] = useState(false);
	const [name, setName] = useState("");
	const [loading, setLoading] = useState(false);
	const [openUpdateOptions, setOpenUpdateOptions] = useState(false);

	const { user } = useUser();

	const defineTags = async () => {
		let allColors = [bg];
		const tags = [];
		config.forEach(({ colors }) => (allColors = [...allColors, ...colors]));

		for (const color of allColors) {
			const cName = await getColorName(color);
			if (tags.indexOf(cName) === -1) tags.push(cName);
		}
		return tags;
	};

	const update = async () => {
		setLoading(true);
		const data = { config, bg, duration, animationType };
		const tags = await defineTags();
		const name = validateString(meshName);
		const newSaveOb = { ...deepCopy(receivedData), data, tags, name };

		await editData("mesh", receivedData.id, newSaveOb);
		setLoading(false);
	};

	const save = async () => {
		const uid = user?.uid || "guest";
		setLoading(true);
		const tags = await defineTags();

		const storeObject = {
			data: { config, bg, duration, animationType },
			love: 0,
			name,
			tags,
			uid,
		};

		saveMesh(storeObject, (res) => {
			if (res.status && res.id) {
				setOpen(false);
			}

			setLoading(false);
		});
	};

	const remove = async () => {
		await deleteData("mesh", receivedData.id, (res) => {
			console.log(res);
		});
	};

	const btnCl = "text-white bg-accent-500";

	const pop = ReactDOM.createPortal(
		<>
			<div
				className={"z fixed inset-0 bg-dark-900/70"}
				onClick={() => setOpen(false)}
			></div>
			<div className="fixed left-1/2 top-1/2 -ml-[180px] -mt-40 flex w-[360px] flex-col gap-3 rounded-lg bg-white p-3 text-dark-800">
				<div className="flex items-center justify-between">
					<div>Save Mesh</div>
					<div
						className="cursor-pointer"
						onClick={() => setOpen(false)}
					>
						{icons.close}
					</div>
				</div>
				<div>
					<input
						placeholder="Mesh Name"
						spellCheck={false}
						className="w-full rounded border border-dark-200 p-2 text-lg"
						value={name}
						onChange={(e) =>
							setName(validateString(e.target.value))
						}
					/>
				</div>
				{user?.uid ? (
					<div className="flex items-center justify-end gap-3">
						{loading && (
							<div className="ml-1 mr-auto h-4 w-4 animate-spin rounded-full border-2 border-dark-200 border-t-dark-800"></div>
						)}
						<Button
							className="border border-dark-200 px-3"
							onClick={() => setOpen(false)}
						>
							Cancel
						</Button>
						<Button className={btnCl + " px-5"} onClick={save}>
							Save
						</Button>
					</div>
				) : (
					<div className="flex items-center rounded bg-accent-100 p-3">
						<div className="flex-1 text-accent-700">
							Signup to save and keep your meshes :)
						</div>
						<div className="ml-2 rounded bg-white text-accent-500">
							<Login className="rounded bg-white pr-1 text-accent-500 hover:opacity-80" />
						</div>
					</div>
				)}
			</div>
		</>,
		document.body
	);

	const upd = useRef(null);

	const updPop = () => {
		const { current } = upd;

		const { top, left, width } = current.getBoundingClientRect();

		return ReactDOM.createPortal(
			<>
				<div
					className="z fixed inset-0"
					onClick={() => setOpenUpdateOptions(false)}
				></div>
				<div className="fixed" style={{ top, left: left + width / 2 }}>
					<div className="absolute bottom-3 left-1/2 -ml-[80px] flex w-[160px] flex-col gap-2 rounded-lg p-2">
						{saveButton}
						{delButton}
					</div>
				</div>
			</>,
			document.body
		);
	};

	useEffect(() => {
		setIsSaving(open);
	}, [open]);

	const saveButton = (
		<Button className={btnCl + " px-5"} onClick={() => setOpen(true)}>
			Save
		</Button>
	);

	const delButton = (
		<Button onClick={remove} className="bg-red-500 px-5 text-white">
			Delete
		</Button>
	);

	const updateButton = (
		<div ref={upd}>
			<Button className={btnCl}>
				{loading && (
					<div className="ml-1 mr-auto h-4 w-4 animate-spin rounded-full border-2 border-dark-800 border-t-dark-100"></div>
				)}
				<div className="flexer h-full pl-4 pr-3" onClick={update}>
					Update
				</div>
				<div
					className="flexer h-full border-l border-dark-800 px-2"
					onClick={() => setOpenUpdateOptions((c) => !c)}
				>
					<div className="scale-50">{icons.arrowDown}</div>
				</div>
			</Button>
		</div>
	);

	const displayButton =
		user && receivedData && user.uid === receivedData.uid
			? updateButton
			: saveButton;

	return (
		<>
			{open && pop}
			{openUpdateOptions && updPop()}
			{displayButton}
		</>
	);
}
